'use client'

import { Animation, Cell, EmptyState, Image, Spacer, Text } from '@vinted/web-ui'
import { isNil, omitBy } from 'lodash'
import { Fragment, ReactNode, useEffect, useState } from 'react'

import { ConversationProvider } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { HorizontalScrollArea } from '@marketplace-web/shared/horizontal-scroll-area'
import { useAsset } from '@marketplace-web/domain/assets'
import { urlWithParams } from '@marketplace-web/shared/utils'
import { ProductItemModel } from '@marketplace-web/domain/product-item-data'
import { getItemPhotoThumbnail } from '@marketplace-web/domain/item-photo-data'

import { clickEvent, clickListItemEvent, impressionEvent } from '_libs/common/event-tracker/events'
import { OurPlatformDisclaimer } from '@marketplace-web/domain/legal-block'
import { BUNDLES_POLICY_URL, CREATE_BUNDLE_URL, MEMBER_PROFILE_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { ListItemContentType } from 'constants/tracking/content-types'
import { Screen } from 'constants/tracking/screens'
import {
  cannotBundleButtonViewEvent,
  createBundleSummary,
  transformBundleSummaryDto,
  ReasonForNotBundling,
} from '@marketplace-web/bundles/items-bundle-data'

import ItemsMultiselect from '../../components/ItemsMultiselect'
import BundleFooter from './BundleFooter'
import BundleHeader from './BundleHeader'
import ElectronicsVerificationBanner from './ElectronicsVerificationBanner'

import useUserBundleItems from './hooks/useUserBundleItems'
import OfflineVerificationBanner from './OfflineVerificationBanner'
import {
  getBundleTrackingIdUrlParam,
  getIsBundlingAllowed,
  removeBundleTrackingIdUrlParam,
} from './utils'

type Props = {
  isDigitalRepublicLawEnabled: boolean
  preselectedItemIds: Array<number>
  userId: number
  transactionId: number | null
  homepageSessionId: string | null
  checkoutId: string | null
}

const BundleItems = ({
  isDigitalRepublicLawEnabled,
  preselectedItemIds,
  userId: sellerId,
  transactionId,
  homepageSessionId,
  checkoutId,
}: Props) => {
  const [isSelectedItemsUpdatedFromUrl, setIsSelectedItemsUpdatedFromUrl] = useState(false)
  const [bundleTrackingId, setBundleTrackingId] = useState<string | null>(null)
  const [selectedItems, setSelectedItems] = useState<Array<ProductItemModel>>([])
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const translate = useTranslate('bundle_items')
  const { relativeUrl, replaceHistoryState, urlQuery, searchParams } = useLocation()
  const asset = useAsset('/assets/animations')

  const {
    fetch: fetchSummary,
    transformedData: bundleSummary,
    isLoading: isBundleSummaryLoading,
  } = useFetch(createBundleSummary, response => transformBundleSummaryDto(response.bundle_summary))

  const fetchDebouncedSummary = useDebounce(
    async ({ itemIds }) => {
      await fetchSummary({ itemIds, sellerId })
    },
    500,
    false,
  )

  const {
    items: bundleItems,
    isLoading,
    fetchNextPage,
    endReached,
    offlineVerificationFee,
    containsOfflineVerificationItem,
  } = useUserBundleItems({
    preselectedItemIds,
    userId: sellerId,
    bundleSummary,
  })

  useEffect(() => {
    const trackingId = getBundleTrackingIdUrlParam(urlQuery)

    if (!trackingId) return

    setBundleTrackingId(trackingId)

    const url = removeBundleTrackingIdUrlParam(relativeUrl, urlQuery)

    replaceHistoryState(url)
  }, [relativeUrl, replaceHistoryState, urlQuery])

  useEffect(() => {
    if (!bundleItems.length) return
    if (isSelectedItemsUpdatedFromUrl) return

    const filteredSelectedItems = bundleItems.filter(({ id }) => preselectedItemIds.includes(id))

    setSelectedItems(filteredSelectedItems)
    setIsSelectedItemsUpdatedFromUrl(true)
  }, [bundleItems, isSelectedItemsUpdatedFromUrl, preselectedItemIds, selectedItems, transactionId])

  useEffect(() => {
    if (!transactionId && !isSelectedItemsUpdatedFromUrl) return

    const itemIds = selectedItems.map(({ id }) => id)

    fetchDebouncedSummary({ itemIds })
  }, [fetchDebouncedSummary, isSelectedItemsUpdatedFromUrl, selectedItems, transactionId, sellerId])

  const onSetSelectedItems = (items: Array<ProductItemModel>) => {
    setSelectedItems(items)

    const itemIds = items.map(({ id }) => id)

    const updatedUrl = urlWithParams(CREATE_BUNDLE_URL(sellerId), {
      ...searchParams,
      item_ids: itemIds,
    })

    replaceHistoryState(updatedUrl)
  }

  const onLoadItems = () => {
    if (endReached) return

    fetchNextPage()
  }

  const handleItemVisibility: ComponentProps<typeof ItemsMultiselect>['onItemVisible'] = args => {
    const seenItems: Array<number> = []

    const { item, index } = args

    if (seenItems.includes(item.data.id)) return

    if (
      !getIsBundlingAllowed({ item: item.data, bundleItems, selectedItemId: selectedItems[0]?.id })
    ) {
      const cannotBundleEvent = cannotBundleButtonViewEvent({
        listingId: item.data.id,
        sellerId,
        reasonForNotBundling: ReasonForNotBundling.ShippingSize,
        screen: Screen.Bundling,
        bundleSessionId: bundleTrackingId,
      })
      track(cannotBundleEvent)
    }

    const showItemEvent = impressionEvent({
      id: item.data.id,
      position: index,
      contentType: ListItemContentType.Item,
      contentSource: ContentSource.BundleItems,
      itemOwnerId: item.data.user.id,
    })

    track(showItemEvent)
    seenItems.push(item.data.id)
  }

  const handleItemClick = (item: ProductItemModel, position: number, isSelected: boolean) => {
    track(
      clickListItemEvent({
        position,
        id: item.id,
        contentType: ListItemContentType.Item,
        contentSource: ContentSource.BundleItems,
      }),
    )

    track(
      clickEvent({
        screen: Screen.Bundling,
        target: isSelected
          ? ClickableElement.AddItemToBundle
          : ClickableElement.RemoveItemFromBundle,
        targetDetails: JSON.stringify(
          omitBy(
            {
              item_id: item.id,
              bundle_id: bundleTrackingId,
              transaction_id: transactionId,
            },
            isNil,
          ),
        ),
      }),
    )
  }

  const handlePolicyDescriptionClick = () => {
    track(
      clickEvent({
        target: ClickableElement.BundlesPolicyLink,
      }),
    )
  }

  const renderPolicyDescription = () => {
    const values = {
      'policy-link': (chunks: Array<ReactNode>) => (
        <a
          key="policy-link"
          href={BUNDLES_POLICY_URL}
          target="_blank"
          onClick={handlePolicyDescriptionClick}
          rel="noreferrer"
        >
          {chunks}
        </a>
      ),
    }

    return (
      <>
        <Spacer />
        <Text as="h4" text={translate('policy_description', values)} type={Text.Type.Caption} />
      </>
    )
  }

  const renderContent = () => {
    return (
      <>
        <Spacer size={Spacer.Size.XLarge} />
        <Cell
          styling={breakpoints.tabletsUp ? Cell.Styling.Tight : Cell.Styling.Narrow}
          title={
            <Text as="h1" text={translate('build_bundle_title')} type={Text.Type.Heading} bold />
          }
          body={
            <>
              {renderPolicyDescription()}
              {isDigitalRepublicLawEnabled && <OurPlatformDisclaimer />}
              {containsOfflineVerificationItem && (
                <OfflineVerificationBanner offlineVerificationFee={offlineVerificationFee} />
              )}
              {bundleSummary?.containsElectronicsVerificationItem && (
                <ElectronicsVerificationBanner
                  electronicsVerificationFee={bundleSummary?.electronicsVerificationFee}
                />
              )}
            </>
          }
        />
        <Spacer size={Spacer.Size.Large} />
        <ItemsMultiselect
          items={bundleItems}
          isLoading={isLoading}
          endReached={!!endReached}
          loadItems={onLoadItems}
          selectedItems={selectedItems}
          onItemVisible={handleItemVisibility}
          onItemClick={handleItemClick}
          updateSelectedItems={onSetSelectedItems}
          showFavourite
          showPriceBreakdown={false}
          bundleTrackingId={bundleTrackingId}
          sellerId={sellerId}
          emptyState={
            <EmptyState
              animation={<Animation animationUrl={asset('closet-empty-state.json')} />}
              title={translate('empty_state.multiselect.title')}
              body={translate('empty_state.multiselect.body')}
            />
          }
        />
      </>
    )
  }

  const renderPreFooter = () => {
    if (!selectedItems.length) return null

    return (
      <div
        className="container u-phones-only u-ui-padding-bottom-medium"
        data-testid="bundle-items-thumbnails"
      >
        <Spacer />
        <HorizontalScrollArea showControls={false}>
          <div className="u-nowrap">
            {selectedItems.map(item => (
              <Fragment key={item.id}>
                <Image
                  key={item.id}
                  src={getItemPhotoThumbnail(item.photos)}
                  styling={Image.Styling.Rounded}
                  size={Image.Size.Medium}
                  scaling={Image.Scaling.Cover}
                />
                <Spacer orientation={Spacer.Orientation.Vertical} />
              </Fragment>
            ))}
          </div>
        </HorizontalScrollArea>
      </div>
    )
  }

  const isSellerBusiness = bundleItems[0]?.user.isBusiness || false

  return (
    <div className="u-flexbox u-flex-direction-column u-fill-viewport">
      <div className="action-header">
        <div className="container">
          <BundleHeader
            pathBack={MEMBER_PROFILE_URL(sellerId)}
            discountHint={bundleSummary?.discountText}
          />
        </div>
      </div>
      <div className="u-flex-grow">
        <div className="container">{renderContent()}</div>
      </div>
      <div className="action-footer">
        {renderPreFooter()}
        <ConversationProvider>
          <div className="container">
            <BundleFooter
              bundleSummary={bundleSummary}
              transactionId={transactionId}
              selectedItems={selectedItems}
              bundleTrackingId={bundleTrackingId}
              isSellerBusiness={isSellerBusiness}
              homepageSessionId={homepageSessionId}
              sellerId={sellerId}
              isBundleSummaryLoading={isBundleSummaryLoading}
              checkoutId={checkoutId}
            />
          </div>
        </ConversationProvider>
      </div>
    </div>
  )
}

export default BundleItems
